import {
    CreateKnowledgeBaseRequest,
    KnowledgeBaseResult,
    KnowledgeBaseService,
  } from '@/api/';
import { Bytes } from 'firebase/firestore';
  import { action, atom, onSet } from 'nanostores';
  
  /**
   * The current selected workspace
   */
  export const $knowledgebase = atom<KnowledgeBaseResult | undefined>();
  
  /**
   * The workspaces for the current selected user and org.
   */
  export const $knowledgebases = atom<KnowledgeBaseResult[]>([]);
  
  /**
   * Main function to load knowledgebases
   */
  const loadKnowledgeBases = async (orgId: string) => {
    if (orgId) {
      console.log('Retrieving knowledgebases...');
  
      await ensureOrganization(orgId);
  
      var knowledgebases = await KnowledgeBaseService.getKnowledgeBases(orgId);

      $knowledgebases.set(knowledgebases);
  
      // set the workspace ids in local storage since we are gonna use it later
      // for drag and drop, these workspace ids will act as order of the workspaces
      const existingWorkspaceOrders = JSON.parse(
        localStorage.getItem('knowledgebases_orders') ?? '{}',
      );
      existingWorkspaceOrders[orgId] = knowledgebases.map(w => w.knowledgeBase.id);
      localStorage.setItem(
        'knowledgebases_orders',
        JSON.stringify(existingWorkspaceOrders),
      );
    }
  };
  
  /**
   * Reload workspaces on org change
   */
  $org.subscribe(async org => {
    $knowledgebases.set([]);
  
    await loadKnowledgeBases(org?.id ?? '');
  });
  
  /**
   * When the kno changes, we subscribe for notifications.
   */
  onSet($knowledgebase, async kb => {
    if (!kb.newValue) {
      return;
    }
  
    if (kb.newValue.knowledgeBase.id == $knowledgebase.get()?.knowledgeBase.id) {
      return;
    }
  
    await signalRService.joinWorkspaceGroup(kb.newValue.knowledgeBase.id);
  });
  
  /**
   * Watch for route changes; if we haven't loaded the orgs, we try to load them.
   */
  route.subscribe(async name => {
    if (name !== 'knowledgebases' || ($knowledgebases.value?.length ?? 0) > 0) {
      return;
    }
  
    const params = $router.get()?.params as Record<'organizationId', string>;
  
    await loadKnowledgeBases(params.organizationId);
  });
  
  /**
   * Create a new workspace
   * TODO: Include profile selection/invites.
   */
  export const createKnowledgeBase = action(
    $knowledgebase,
    'createKnowledgeBase',
    async (store, name: string, fileContent: File, fileName: string, description?: string) => {
      const params = $router.get()?.params as Record<'organizationId', string>;
  
      const request: CreateKnowledgeBaseRequest = {
        name,
        description,
        organizationId: params.organizationId,
        fileContentBase64: await file2Base64(fileContent),
        fileName,
      };
  
      const response = await KnowledgeBaseService.createKnowledgeBase(request);

      if (!response.knowledgeBase) {
        // error is displayed by caller
        return response;
      }

      const result: KnowledgeBaseResult = {
        knowledgeBase: response.knowledgeBase,
      };

      $knowledgebases.set([result, ...$knowledgebases.get()]);
  
      $knowledgebase.set(result);
    },
  );
  
  /**
   * Delete's the selected workspace.
   */
  export const deleteKnowledgeBase = action(
    $knowledgebase,
    'deleteKnowledgeBase',
    async (store, knowledgeBaseId: string) => {
      await KnowledgeBaseService.deleteKnowledgeBase(knowledgeBaseId);
  
      $knowledgebase.set(undefined);
  
      // Splice out the project
      const knowledgebases = $knowledgebases.get();
  
      const index = knowledgebases.findIndex(w => w.knowledgeBase.id === knowledgeBaseId);
  
      if (index > -1) {
        knowledgebases.splice(index, 1);
  
        $knowledgebases.set(knowledgebases);
      }
    },
  );

  /**
   * Ensure that the knowledgebase is loaded and selected.
   */
  export const ensureKnowledgeBase = action(
    $knowledgebase,
    'ensureKnowledgeBase',
    async (store, knowledgeBaseId: string) => {
      let ws: KnowledgeBaseResult | undefined;
  
      const params = $router.get()?.params as Record<'organizationId', string>;
  
      if ($knowledgebases.get().length === 0) {
        const knowledgebases = await KnowledgeBaseService.getKnowledgeBases(
          params.organizationId ?? '',
        );
  
        $knowledgebases.set(knowledgebases);
  
        ws = knowledgebases.find(w => w.knowledgeBase.id === knowledgeBaseId);
      } else {
        ws = $knowledgebases.get().find(w => w.knowledgeBase.id === knowledgeBaseId);
      }
  
      $knowledgebase.set(ws);
    },
  );
  